import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import { CardDisplaySettings, CardFieldKey, DeckRenderMode, PipelineItem, SECTION_COLORS, SECTION_LABELS } from '../types';
import { CANVAS_W, CANVAS_H, useDeckAutoPan } from './DeckPage';
import {
  formatEstLaunchPrice,
  formatLandCost,
  formatTargetPreview,
  normalizeProjectName,
  normalizeEstLaunchPrice,
  normalizeLandCost,
  normalizeTargetPreview,
} from '../utils/displayFormat';
import { handlePlainDrop, handlePlainLineBreak, handlePlainPaste, handlePlainTextInput, normalizeEditableText } from '../utils/editableText';

export const CARD_W = 230;
export const CARD_HEADER_HEIGHT = 14;
export const CARD_HEADER_FONT_SIZE = 6;
export const CARD_HEADER_LETTER_SPACING = 0.3;
export const CARD_HEADER_VERTICAL_PADDING = 0;
const CARD_BODY_FONT_SIZE = 9.5;
const PROJECT_NAME_FONT_SIZE = 11.5;

const normalizeFieldFontSize = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(24, Math.max(6, parsed));
};
export const DEFAULT_VISIBLE_CARD_FIELDS: CardFieldKey[] = ['projectName', 'targetPreview', 'estUnits', 'developer', 'landCost'];

interface DeckCardProps {
  item: PipelineItem;
  /** Card top-left in canvas px. */
  x: number;
  y: number;
  width?: number;
  height?: number;
  aspectLocked?: boolean;
  draggable?: boolean;
  onDragEnd?: (itemId: number, xPct: number, yPct: number) => void;
  /** Live position update while dragging (keeps leader line attached). */
  onDragMove?: (itemId: number, xPct: number, yPct: number) => void;
  onDragCancel?: (itemId: number) => void;
  /** Page-only cards may edit their displayed text without touching Overview. */
  dataEditable?: boolean;
  onItemChange?: (item: PipelineItem) => void;
  allowedSections?: PipelineItem['section'][];
  onContextMenu?: (itemId: number, event: React.MouseEvent<HTMLDivElement>) => void;
  onDoubleClick?: (itemId: number, event: React.MouseEvent<HTMLDivElement>) => void;
  onEditEnd?: (itemId: number) => void;
  onSizeChange?: (itemId: number, height: number) => void;
  /** Reports the final unscaled print footprint after the content is measured. */
  onPrintMeasure?: (itemId: number, height: number) => void;
  onResizeStart?: (itemId: number) => void;
  onResizeEnd?: (itemId: number) => void;
  headerColor?: string;
  /** Diagnostic state for projects that cannot be connected to a marker. */
  connectorStatus?: 'connected' | 'missing-coordinates' | 'no-safe-route';
  connectorReason?: string;
  launchingSoonColor?: string;
  showLaunchingSoonBorder?: boolean;
  onResize?: (itemId: number, widthPct: number, heightPct: number) => void;
  displaySettings?: CardDisplaySettings;
  selected?: boolean;
  onSelect?: (itemId: number, additive: boolean, phase?: 'down' | 'click', wasSelected?: boolean) => void;
  renderMode?: DeckRenderMode;
  /** Scale applied to the fixed header only; body scaling remains independent. */
  headerScale?: number;
}

/**
 * A deck info card ("GLS – CHUAN GROVE / Target Preview … / Land Cost …").
 * Draggable inside the 1600×900 canvas; positions are reported in % so they
 * stay stable however the preview is scaled.
 */
export const DeckCard: React.FC<DeckCardProps> = ({
  item,
  x,
  y,
  width = CARD_W,
  height,
  aspectLocked = false,
  draggable = false,
  onDragEnd,
  onDragMove,
  onDragCancel,
  dataEditable = false,
  onItemChange,
  allowedSections = [],
  onContextMenu,
  onDoubleClick,
  onEditEnd,
  onSizeChange,
  onPrintMeasure,
  onResizeStart,
  onResizeEnd,
  headerColor,
  connectorStatus,
  connectorReason,
  launchingSoonColor = '#111111',
  showLaunchingSoonBorder = true,
  onResize,
  displaySettings,
  selected = false,
  onSelect,
  renderMode = 'interactive',
  headerScale = 1,
}) => {
  const colors = SECTION_COLORS[item.section];
  const [naturalHeight, setNaturalHeight] = useState(145);
  const [contentScale, setContentScale] = useState(1);
  const [verticalScale, setVerticalScale] = useState(1);
  const printMode = renderMode === 'print';
  const effectiveHeaderScale = Math.min(2, Math.max(0.4, Number.isFinite(headerScale) ? headerScale : 1));
  const roadOnly = !item.projectName?.trim() && !!item.roadName?.trim();
  // The user-defined card height is authoritative. Natural height is only a
  // fallback for cards that have not received an explicit dimension yet.
  const displayHeight = height ?? naturalHeight;
  const dragState = useRef<{
    mode: 'move' | 'resize'; startX: number; startY: number; origX: number; origY: number;
    width: number; height: number; scale: number; moved: boolean; wasSelected: boolean; startScrollLeft: number; startScrollTop: number;
  } | null>(null);
  const pointerFrame = useRef<number | null>(null);
  const pendingPointerUpdate = useRef<(() => void) | null>(null);
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const contentMeasureRef = useRef<HTMLDivElement | null>(null);
  const contentScaleRef = useRef(1);
  const autoPan = useDeckAutoPan();
  const field = (key: CardFieldKey) => displaySettings?.fields?.[key] ?? {};
  const measurementKey = useMemo(() => JSON.stringify({
    item,
    displaySettings,
    width,
    height,
    dataEditable,
    renderMode,
  }), [dataEditable, displaySettings, height, item, renderMode, width]);
  const visible = (key: CardFieldKey) => {
    const settings = displaySettings?.fields?.[key];
    return settings?.visible ?? DEFAULT_VISIBLE_CARD_FIELDS.includes(key);
  };
  const fieldSx = (key: CardFieldKey, defaultSize: number) => {
    const settings = field(key);
    return {
      fontSize: normalizeFieldFontSize(settings.fontSize, defaultSize) * contentScale,
      ...(settings.color ? { color: settings.color } : {}),
      ...(settings.bold != null ? { fontWeight: settings.bold ? 800 : 400 } : {}),
      ...(settings.italic != null ? { fontStyle: settings.italic ? 'italic' : 'normal' } : {}),
      ...(settings.highlight ? { backgroundColor: settings.highlight } : {}),
      ...(dataEditable ? { whiteSpace: 'pre-line' as const } : {}),
    };
  };

  useLayoutEffect(() => {
    contentScaleRef.current = 1;
    setContentScale(1);
    setVerticalScale(1);
  }, [measurementKey]);

  useLayoutEffect(() => {
    const node = contentRef.current;
    const headerNode = headerRef.current;
    const measureNode = contentMeasureRef.current;
    if (!node || !headerNode || !measureNode) return;
    const measure = () => {
      const measuredNaturalHeight = headerNode.offsetHeight + measureNode.offsetHeight;
      setNaturalHeight((current) => current === measuredNaturalHeight ? current : measuredNaturalHeight);
      const availableHeight = Math.max(1, node.clientHeight - headerNode.offsetHeight);
      const availableWidth = Math.max(1, node.clientWidth);
      const requiredHeight = Math.max(1, measureNode.offsetHeight);
      const requiredWidth = Math.max(1, measureNode.scrollWidth);
      const currentScale = contentScaleRef.current;
      let nextScale = currentScale;
      const heightOverflow = requiredHeight > availableHeight + 2;
      const widthOverflow = requiredWidth > availableWidth + 2;
      const heightRatio = requiredHeight / availableHeight;
      const widthRatio = widthOverflow ? requiredWidth / availableWidth : 1;
      const verticalRatio = availableHeight / Math.max(1, requiredHeight);
      const verticalScaleAllowed = verticalRatio >= 0.9 && verticalRatio <= 1.1;
      const fittingRatio = Math.max(widthRatio, verticalScaleAllowed || !heightOverflow ? 1 : heightRatio);
      if (fittingRatio > 1.005) {
        // Keep reducing until the measured content fits. There is deliberately
        // no readable-size floor because fixed card dimensions are authoritative.
        nextScale = currentScale * Math.min(0.96, 0.97 / fittingRatio);
      } else if (!widthOverflow && !heightOverflow && verticalRatio > 1.1 && currentScale < 1) {
        nextScale = Math.min(1, currentScale * 1.04);
      }
      const nextVerticalScale = verticalScaleAllowed
        ? verticalRatio
        : 1;
      setVerticalScale((current) => Math.abs(current - nextVerticalScale) > 0.005 ? nextVerticalScale : current);
      const scaleChanged = fittingRatio > 1.005
        ? nextScale < currentScale
        : Math.abs(nextScale - currentScale) > 0.005;
      if (scaleChanged) {
        contentScaleRef.current = nextScale;
        setContentScale(nextScale);
      }
      if (printMode) onPrintMeasure?.(item.id, displayHeight);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(measureNode);
    return () => observer.disconnect();
  }, [displayHeight, height, item.id, measurementKey, onPrintMeasure, printMode]);

  useLayoutEffect(() => {
    onSizeChange?.(item.id, displayHeight);
  }, [displayHeight, item.id, onSizeChange]);

  const schedulePointerUpdate = (update: () => void) => {
    pendingPointerUpdate.current = update;
    if (pointerFrame.current != null) return;
    pointerFrame.current = window.requestAnimationFrame(() => {
      pointerFrame.current = null;
      const next = pendingPointerUpdate.current;
      pendingPointerUpdate.current = null;
      next?.();
    });
  };

  const flushPointerUpdate = () => {
    if (pointerFrame.current != null) window.cancelAnimationFrame(pointerFrame.current);
    pointerFrame.current = null;
    const next = pendingPointerUpdate.current;
    pendingPointerUpdate.current = null;
    next?.();
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if (!draggable || e.button !== 0) return;
    const wasSelected = selected;
    if (onSelect) onSelect(item.id, e.shiftKey, 'down', wasSelected);
    const node = nodeRef.current;
    const canvas = node?.closest('[data-pipeline-page]') as HTMLElement | null;
    if (!node || !canvas) return;
    // The canvas is CSS-scaled to fit the viewport; convert screen px → canvas px.
    const scale = canvas.getBoundingClientRect().width / CANVAS_W;
    const scrollHost = node.closest('[data-deck-scroll]') as HTMLElement | null;
    const resize = !!(e.target as HTMLElement).closest('[data-card-resize]');
    dragState.current = {
      mode: resize ? 'resize' : 'move',
      startX: e.clientX, startY: e.clientY, origX: x, origY: y,
      width, height: displayHeight, scale,
      wasSelected,
      startScrollLeft: scrollHost?.scrollLeft ?? 0,
      startScrollTop: scrollHost?.scrollTop ?? 0,
      moved: false,
    };
    if (resize) onResizeStart?.(item.id);
    autoPan.start(e.clientX, e.clientY);
    node.style.willChange = 'transform, width, height';
    node.setPointerCapture(e.pointerId);
    e.preventDefault();
  };

  const currentPct = (clientX: number, clientY: number) => {
    const st = dragState.current!;
    const scrollHost = nodeRef.current?.closest('[data-deck-scroll]') as HTMLElement | null;
    const scrollDeltaX = (scrollHost?.scrollLeft ?? st.startScrollLeft) - st.startScrollLeft;
    const scrollDeltaY = (scrollHost?.scrollTop ?? st.startScrollTop) - st.startScrollTop;
    const nx = st.origX + (clientX - st.startX + scrollDeltaX) / st.scale;
    const ny = st.origY + (clientY - st.startY + scrollDeltaY) / st.scale;
    const clampedX = Math.min(Math.max(nx, 0), CANVAS_W - width);
    const clampedY = Math.min(Math.max(ny, 26), CANVAS_H - displayHeight);
    return { xPct: (clampedX / CANVAS_W) * 100, yPct: (clampedY / CANVAS_H) * 100 };
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    const state = dragState.current;
    if (!state) return;
    autoPan.move(e.clientX, e.clientY);
    if (state.mode === 'resize') {
      const scrollHost = nodeRef.current?.closest('[data-deck-scroll]') as HTMLElement | null;
      const deltaX = (e.clientX - state.startX + (scrollHost?.scrollLeft ?? state.startScrollLeft) - state.startScrollLeft) / state.scale;
      const deltaY = (e.clientY - state.startY + (scrollHost?.scrollTop ?? state.startScrollTop) - state.startScrollTop) / state.scale;
      let nextWidth: number;
      let nextHeight: number;
      if (aspectLocked) {
        const widthFactor = (state.width + deltaX) / state.width;
        const heightFactor = (state.height + deltaY) / state.height;
        const requested = Math.abs(widthFactor - 1) >= Math.abs(heightFactor - 1) ? widthFactor : heightFactor;
        const minFactor = Math.max(120 / state.width, 70 / state.height);
        const maxFactor = Math.min((CANVAS_W - x) / state.width, (CANVAS_H - y) / state.height);
        const factor = Math.min(Math.max(requested, minFactor), maxFactor);
        nextWidth = state.width * factor;
        nextHeight = state.height * factor;
      } else {
        nextWidth = Math.min(Math.max(state.width + deltaX, 120), CANVAS_W - x);
        nextHeight = Math.min(Math.max(state.height + deltaY, 70), CANVAS_H - y);
      }
      schedulePointerUpdate(() => onResize?.(item.id, nextWidth / CANVAS_W * 100, nextHeight / CANVAS_H * 100));
      return;
    }
    if (!state.moved) {
      const distance = Math.hypot(e.clientX - state.startX, e.clientY - state.startY);
      if (distance < 3) return;
      state.moved = true;
    }
    const { xPct, yPct } = currentPct(e.clientX, e.clientY);
    schedulePointerUpdate(() => onDragMove?.(item.id, xPct, yPct));
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!dragState.current) return;
    autoPan.stop();
    flushPointerUpdate();
    if (dragState.current.mode === 'resize') {
      dragState.current = null;
      onResizeEnd?.(item.id);
      nodeRef.current?.style.removeProperty('will-change');
      return;
    }
    if (!dragState.current.moved) {
      onSelect?.(item.id, e.shiftKey, 'click', dragState.current.wasSelected);
      dragState.current = null;
      nodeRef.current?.style.removeProperty('will-change');
      return;
    }
    const { xPct, yPct } = currentPct(e.clientX, e.clientY);
    dragState.current = null;
    onDragEnd?.(item.id, xPct, yPct);
    nodeRef.current?.style.removeProperty('will-change');
  };

  return (
    <Box
      ref={nodeRef}
      title={connectorStatus === 'missing-coordinates'
        ? `Connector unavailable: ${connectorReason ?? 'this project has no valid map coordinates'}`
        : connectorStatus === 'no-safe-route'
          ? `Connector unavailable: ${connectorReason ?? 'no safe orthogonal route was found around cards, markers, title, legend, footer, or reserved connector corridors'}`
          : undefined}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={() => {
        autoPan.stop();
        flushPointerUpdate();
        if (dragState.current?.mode === 'resize') onResizeEnd?.(item.id);
        else if (dragState.current?.mode === 'move') onDragCancel?.(item.id);
        dragState.current = null;
        nodeRef.current?.style.removeProperty('will-change');
      }}
      onContextMenu={(event) => onContextMenu?.(item.id, event)}
      onDoubleClick={(event) => onDoubleClick?.(item.id, event)}
      onBlur={(event) => {
        if (!dataEditable || event.currentTarget.contains(event.relatedTarget as Node | null)) return;
        onEditEnd?.(item.id);
      }}
      sx={{
        position: 'absolute',
        left: x,
        top: y,
        width,
        height: displayHeight,
        minWidth: 0,
        minHeight: 0,
        // Keep the resize handle (which sits just outside the card edge)
        // visible; the content surface below clips text to the card bounds.
        overflow: 'visible',
        cursor: draggable ? 'grab' : 'default',
        userSelect: 'none',
        touchAction: 'none',
        '&:active': draggable ? { cursor: 'grabbing' } : undefined,
        ...(selected ? { outline: '2px solid #f5d76e', outlineOffset: 3 } : {}),
      }}
      data-pipeline-card="true"
    >
      <Box sx={{ width, height: displayHeight, overflow: 'hidden', borderRadius: '6px' }}>
        <Box
          ref={contentRef}
          sx={{
            width,
            maxWidth: width,
            height: displayHeight,
            borderRadius: '6px',
            overflowY: 'hidden',
            overflowX: 'hidden',
            fontFamily: "'Liberation Sans', sans-serif",
            fontSynthesis: 'none',
            backgroundColor: '#ffffff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
            border: item.isLaunchingSoon && showLaunchingSoonBorder
              ? `2.5px solid ${launchingSoonColor}`
              : '1px solid #c7ccd4',
          }}
        >
      <Box
        ref={headerRef}
        title={dataEditable && allowedSections.length > 1
          ? `Map section: ${SECTION_LABELS[item.section]}. Click to change section. Market region (CCR/RCR/OCR) is a separate Overview field.`
          : `Map section: ${SECTION_LABELS[item.section]}. This controls the card and connector colour; CCR/RCR/OCR is a separate market-region field.`}
        onPointerDown={(event) => dataEditable && event.stopPropagation()}
        onClick={() => {
          if (!dataEditable || allowedSections.length < 2) return;
          const index = allowedSections.indexOf(item.section);
          onItemChange?.({ ...item, section: allowedSections[(index + 1) % allowedSections.length] });
        }}
        sx={{
          backgroundColor: headerColor ?? colors.bg,
          color: colors.fg,
          position: 'relative',
          height: CARD_HEADER_HEIGHT * effectiveHeaderScale,
          boxSizing: 'border-box',
          fontSize: CARD_HEADER_FONT_SIZE * effectiveHeaderScale,
          fontWeight: 800,
          letterSpacing: CARD_HEADER_LETTER_SPACING * effectiveHeaderScale,
          textAlign: 'center',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'clip',
          lineHeight: 1,
          py: `${CARD_HEADER_VERTICAL_PADDING * effectiveHeaderScale}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: dataEditable && allowedSections.length > 1 ? 'pointer' : undefined,
        }}
      >
        {SECTION_LABELS[item.section]}
        </Box>
      <Box
        ref={contentMeasureRef}
        sx={{
          width: '100%',
          boxSizing: 'border-box',
          transform: verticalScale === 1 ? undefined : `scaleY(${verticalScale})`,
          transformOrigin: 'top center',
        }}
      >
      <Box sx={{
        px: `${8 * contentScale}px`,
        py: `${4.8 * contentScale}px`,
        pb: `${8 * contentScale}px`,
        textAlign: 'center',
      }}>
        {visible('projectName') && (item.projectName || (!item.projectName && item.roadName)) && <Box
          sx={{ fontWeight: 800, color: '#000000', lineHeight: 1.15, ...fieldSx('projectName', PROJECT_NAME_FONT_SIZE) }}
        >
          <Box
            component="span"
            contentEditable={dataEditable}
            suppressContentEditableWarning
            onKeyDown={handlePlainLineBreak}
            onBeforeInput={handlePlainTextInput}
            onPaste={handlePlainPaste}
            onDrop={handlePlainDrop}
            onPointerDown={(event) => dataEditable && event.stopPropagation()}
            onBlur={(event) => {
              const value = normalizeProjectName(normalizeEditableText(event.currentTarget.innerText || event.currentTarget.textContent));
              if (roadOnly) {
                onItemChange?.({ ...item, projectName: null, roadName: value, hasProjectName: false });
              } else {
                onItemChange?.({ ...item, projectName: value, hasProjectName: Boolean(value) });
              }
            }}
          >
            {normalizeProjectName(item.projectName) || item.roadName}
            {item.hasProjectName && normalizeProjectName(item.projectName) ? '\u00a0*' : ''}
          </Box>
        </Box>}
        {visible('roadName') && item.projectName && item.roadName && <Box
          sx={{ fontWeight: item.projectName ? 400 : 800, fontStyle: item.projectName ? 'italic' : 'normal', color: '#000000', lineHeight: 1.2, ...fieldSx('roadName', CARD_BODY_FONT_SIZE) }}
          contentEditable={dataEditable}
          suppressContentEditableWarning
          onKeyDown={handlePlainLineBreak}
          onBeforeInput={handlePlainTextInput}
          onPaste={handlePlainPaste}
          onDrop={handlePlainDrop}
          onPointerDown={(event) => dataEditable && event.stopPropagation()}
          onBlur={(event) => onItemChange?.({ ...item, roadName: normalizeEditableText(event.currentTarget.innerText || event.currentTarget.textContent).replace(/^Road Name:\s*/i, '').trim().replace(/^–$/, '') || null })}
        >
          {`Road Name: ${item.roadName}`}
        </Box>}
        {visible('status') && item.status && <Box sx={{ color: '#000000', lineHeight: 1.2, ...fieldSx('status', 9.5) }}>
          {item.status}
        </Box>}
        {visible('targetPreview') && <Box
          sx={{ fontWeight: 800, color: '#e01f1f', mt: 0.3, ...fieldSx('targetPreview', 9.5) }}
          contentEditable={dataEditable}
          suppressContentEditableWarning
          onKeyDown={handlePlainLineBreak}
          onBeforeInput={handlePlainTextInput}
          onPaste={handlePlainPaste}
          onDrop={handlePlainDrop}
          onPointerDown={(event) => dataEditable && event.stopPropagation()}
          onBlur={(event) => onItemChange?.({ ...item, targetPreview: normalizeTargetPreview(normalizeEditableText(event.currentTarget.innerText || event.currentTarget.textContent)) })}
        >
          {item.previewConfirmed ? 'Showflat Preview' : 'Target Preview'}: {formatTargetPreview(item.targetPreview)}
        </Box>}
        <Box sx={{ fontSize: CARD_BODY_FONT_SIZE * contentScale, color: '#000000', lineHeight: 1.35, mt: `${2.4 * contentScale}px` }}>
          {visible('district') && <div
            style={{
              ...(fieldSx('district', 9) as React.CSSProperties),
              whiteSpace: 'normal',
              overflowWrap: 'anywhere',
            }}
            contentEditable={dataEditable}
            suppressContentEditableWarning
            onKeyDown={handlePlainLineBreak}
            onBeforeInput={handlePlainTextInput}
            onPaste={handlePlainPaste}
            onDrop={handlePlainDrop}
            onPointerDown={(event) => dataEditable && event.stopPropagation()}
            onBlur={(event) => onItemChange?.({ ...item, district: normalizeEditableText(event.currentTarget.innerText || event.currentTarget.textContent).replace(/^District:\s*/i, '').trim().replace(/^–$/, '') || null })}
          >District: {item.district || '–'}</div>}
          {visible('tenure') && <div
            style={fieldSx('tenure', 9) as React.CSSProperties}
            contentEditable={dataEditable}
            suppressContentEditableWarning
            onKeyDown={handlePlainLineBreak}
            onBeforeInput={handlePlainTextInput}
            onPaste={handlePlainPaste}
            onDrop={handlePlainDrop}
            onPointerDown={(event) => dataEditable && event.stopPropagation()}
            onBlur={(event) => onItemChange?.({ ...item, tenure: normalizeEditableText(event.currentTarget.innerText || event.currentTarget.textContent).replace(/^Tenure:\s*/i, '').trim().replace(/^–$/, '') || null })}
          >Tenure: {item.tenure || '–'}</div>}
          {visible('estUnits') && <div
            style={fieldSx('estUnits', 9) as React.CSSProperties}
            contentEditable={dataEditable}
            suppressContentEditableWarning
            onKeyDown={handlePlainLineBreak}
            onBeforeInput={handlePlainTextInput}
            onPaste={handlePlainPaste}
            onDrop={handlePlainDrop}
            onPointerDown={(event) => dataEditable && event.stopPropagation()}
            onBlur={(event) => onItemChange?.({ ...item, estUnits: normalizeEditableText(event.currentTarget.innerText || event.currentTarget.textContent).replace(/^Est\.\s*/i, '').replace(/\s*Units$/i, '').trim().replace(/^–$/, '') || null })}
          >Est. {item.estUnits || '–'} Units</div>}
          {visible('developer') && <div
            style={fieldSx('developer', 9) as React.CSSProperties}
            contentEditable={dataEditable}
            suppressContentEditableWarning
            onKeyDown={handlePlainLineBreak}
            onBeforeInput={handlePlainTextInput}
            onPaste={handlePlainPaste}
            onDrop={handlePlainDrop}
            onPointerDown={(event) => dataEditable && event.stopPropagation()}
            onBlur={(event) => onItemChange?.({ ...item, developer: normalizeEditableText(event.currentTarget.innerText || event.currentTarget.textContent).replace(/^Developer:\s*/i, '').trim().replace(/^–$/, '') || null })}
          >{item.developer || '–'}</div>}
          {!item.landAwarded && <Box sx={{ color: '#d32f2f', fontStyle: 'italic', fontSize: CARD_BODY_FONT_SIZE * contentScale, lineHeight: 1.2 }}>The land has not been awarded</Box>}
        </Box>
        {visible('landCost') && <Box
          sx={{ fontWeight: 800, color: '#000000', mt: `${2.4 * contentScale}px`, borderTop: `${Math.max(0.5, contentScale)}px solid #e3e7ee`, pt: `${2.4 * contentScale}px`, ...fieldSx('landCost', CARD_BODY_FONT_SIZE) }}
          contentEditable={dataEditable}
          suppressContentEditableWarning
          onKeyDown={handlePlainLineBreak}
          onBeforeInput={handlePlainTextInput}
          onPaste={handlePlainPaste}
          onDrop={handlePlainDrop}
          onPointerDown={(event) => dataEditable && event.stopPropagation()}
          onBlur={(event) => onItemChange?.({ ...item, landCost: normalizeLandCost(normalizeEditableText(event.currentTarget.innerText || event.currentTarget.textContent)) })}
        >
          {item.sourceType === 'LANDSALE' ? 'Land Cost' : 'Est. Land Cost'} {formatLandCost(item.landCost)}
          {formatLandCost(item.landCost) === 'NIL' ? '' : ' psf ppr'}
        </Box>}
        {visible('estLaunchPrice') && <Box
          sx={{ fontWeight: 700, color: '#000000', ...fieldSx('estLaunchPrice', CARD_BODY_FONT_SIZE) }}
          contentEditable={dataEditable}
          suppressContentEditableWarning
          onKeyDown={handlePlainLineBreak}
          onBeforeInput={handlePlainTextInput}
          onPaste={handlePlainPaste}
          onDrop={handlePlainDrop}
          onPointerDown={(event) => dataEditable && event.stopPropagation()}
          onBlur={(event) => onItemChange?.({ ...item, estLaunchPrice: normalizeEstLaunchPrice(normalizeEditableText(event.currentTarget.innerText || event.currentTarget.textContent)) })}
        >
          Est. Avg Launch {formatEstLaunchPrice(item.estLaunchPrice)}
        </Box>}
      </Box>
      </Box>
      </Box>
      </Box>
      {draggable && selected && (
        <Box
          data-card-resize
          title={aspectLocked ? 'Drag to resize card proportionally' : 'Drag to resize card freely'}
          sx={{
            position: 'absolute', right: -4, bottom: -4, width: 11, height: 11,
            bgcolor: '#ffffff', border: '2px solid #1f2a44', borderRadius: '50%',
            cursor: 'nwse-resize', boxShadow: '0 1px 3px rgba(0,0,0,0.35)', zIndex: 3,
          }}
        />
      )}
    </Box>
  );
};
